import { utils as coreUtils } from '@wsb/guac-widget-core';

// Shared color-mode resolution for the base Default theme. Color-mode themes
// (e.g. Aurora/layout31) attach generated per-mode CSS variables to their color
// dials; base typographies read them through `colorModeColor` so that a single
// wiring lights up every color-mode theme, while non-color-mode themes fall back
// to their existing legacy color tokens unchanged.
//
// layout31's `Theme/treatments/utils.js` + `Theme/publish/colorModeStyles.js`
// import from here so there is one source of truth for the resolution.
//
// USAGE — inside a theme method or typography (where `this` is the theme),
// resolve a color from the core CSS-var name plus the legacy token to fall back
// to. The result is dropped straight into a style value:
//
//   import { colorModeColor, COLOR_MODE_CSS_VAR_NAMES } from '../../utils/colorMode';
//   const headingVar = COLOR_MODE_CSS_VAR_NAMES.typography.heading; // '--wsb-heading'
//   // ...
//   style: { color: colorModeColor(this, headingVar, 'highContrast') }
//
//   //  -> '!var(--wsb-heading)'  when a color mode is active for `this` theme
//   //  -> 'highContrast'         for a legacy (non-color-mode) theme
//
// Pass the legacy token that the theme used BEFORE color modes as `fallback`, so
// non-color-mode themes stay byte-identical. Available var names live on
// `COLOR_MODE_CSS_VAR_NAMES` ({ typography: { heading, body, muted }, link: { fg,
// hover, active }, card: {...}, button: {...}, body: { bg, border }, ... }).

export const COLOR_MODE_CSS_VAR_NAMES = (coreUtils && coreUtils.COLOR_MODE_CSS_VAR_NAMES) || {};

const DEFAULT_COLOR_MODE = 'light1';

// Returns the map of generated CSS variables for the theme's active color mode,
// or null when the theme has no color-mode dials (i.e. a legacy paint-job theme).
export function getColorModeCssVariables(theme, colorModeOverride) {
  const base = theme && theme.base;
  const colorMode = colorModeOverride || (base && base.colorMode) || DEFAULT_COLOR_MODE;
  const colorDials = (base && base.colorDials) || [];

  for (let index = 0; index < colorDials.length; index += 1) {
    const dial = colorDials[index];
    const vars =
      dial &&
      dial.config &&
      dial.config.colorModeCssVariables &&
      dial.config.colorModeCssVariables[colorMode];
    if (vars) {
      return vars;
    }
  }

  return null;
}

export function hasColorModeCssVariable(theme, cssVarName, colorModeOverride) {
  const vars = getColorModeCssVariables(theme, colorModeOverride);
  return Boolean(vars && cssVarName && vars[cssVarName]);
}

// The `!var(...)` prefix is Maniless syntax that emits a raw `var(--x)` CSS
// value rather than mapping the string through the legacy color token table.
export function cssVarValue(cssVarName) {
  return cssVarName ? `!var(${cssVarName})` : null;
}

// Resolve a color for a style: the generated color-mode variable when the theme
// has one for the active mode, otherwise the legacy token `fallback` (so legacy
// themes render exactly as before).
export function colorModeColor(theme, cssVarName, fallback) {
  return hasColorModeCssVariable(theme, cssVarName) ? cssVarValue(cssVarName) : fallback;
}
